// Клавиатурна навигация в решетките на работните листове — ЕДИНСТВЕНИЯТ
// механизъм за четирите листа (Доставка, Изписване, Инвентаризация,
// Прехвърляне). Глобалните клавиши на листа (Ctrl+Enter, Esc стълбицата,
// машината за писане) са в `useWorksheetKeyboard`, търсачката — в
// `useWorksheetSearch`, „тежката" клетка с падащо меню — в `PickCell`.
//
// Правилата са ЕДНИ И СЪЩИ навсякъде:
//   ↑/↓        — същата колона, ред нагоре/надолу; от първия ред нагоре →
//                излизане към търсачката (`onLeaveTop`); ↓ на последния ред
//                не прави нищо
//   ←/→        — съседна СЪЩЕСТВУВАЩА клетка; в текстово поле само когато
//                курсорът е в съответния край (иначе стрелката движи курсора)
//   Enter      — надолу в същата колона (както в Excel); колоните от
//                `enterOpensCols` се обслужват от самата клетка (отварят меню)
//   Esc        — излизане от решетката към търсачката (`onEscape`)
//   Ctrl/Cmd+Enter — НЕ е на решетката: минава към глобалния запис на листа
//
// Клетките се намират по id `{prefix}-{ключ на реда}-{колона}` и носят
// `data-rk`/`data-c`, затова един handler на скрол контейнера обслужва цялата
// решетка. Ключът на реда е СТАБИЛЕН (продукт или ключ на реда), не индекс —
// индексите се разминават при премахване и пренареждане на редове.
import { useCallback, useMemo, useRef } from "react";

export type GridRowKey = string | number;

type Params = {
  prefix: string; // напр. „wox" / „trx" / „dwx" / „stx"
  colCount: number;
  // Ключовете на редовете в реда, в който се виждат (ред → следващ ред).
  // Функция, а не масив — извиква се чак при натискане на клавиш, затова
  // навигацията може да се създаде ПРЕДИ редовете (те често идват от hook,
  // който сам иска `focusCell`), а хиляди редове не се копират на всеки рендер.
  getRowKeys: () => GridRowKey[];
  onEscape: () => void; // Esc от решетката (обикновено фокус в търсачката)
  onLeaveTop: () => void; // ↑ от първия ред
  // Колони, чиито клетки сами обработват Enter (падащо меню в клетката)
  enterOpensCols?: number[];
};

// Курсорът е в края на текстовото поле → стрелката може да напусне клетката
const atCaretEdge = (element: HTMLElement, side: "left" | "right") => {
  const input = element as HTMLInputElement;
  if (input.tagName !== "INPUT" || input.selectionStart == null) return true;
  if (side === "left")
    return input.selectionStart === 0 && input.selectionEnd === 0;
  return (
    input.selectionStart === input.value.length &&
    input.selectionEnd === input.value.length
  );
};

export const useGridNavigation = ({
  prefix,
  colCount,
  getRowKeys,
  onEscape,
  onLeaveTop,
  enterOpensCols,
}: Params) => {
  const getRowKeysRef = useRef(getRowKeys);
  getRowKeysRef.current = getRowKeys;
  const onEscapeRef = useRef(onEscape);
  onEscapeRef.current = onEscape;
  const onLeaveTopRef = useRef(onLeaveTop);
  onLeaveTopRef.current = onLeaveTop;
  const enterOpens = useMemo(
    () => new Set(enterOpensCols ?? []),
    [enterOpensCols],
  );

  const cellId = useCallback(
    (rowKey: GridRowKey, col: number) => `${prefix}-${rowKey}-${col}`,
    [prefix],
  );

  // Фокус по клетка. Ред без такава колона (рецепта без „Партида",
  // инвентаризация без „Цена") получава най-близката вляво; още нерендиран ред
  // се изчаква няколко кадъра — новият ред се появява чак след re-render.
  const focusCell = useCallback(
    (rowKey: GridRowKey, col: number, attempt = 0) => {
      requestAnimationFrame(() => {
        const focus = (element: HTMLElement) => {
          element.focus();
          (element as HTMLInputElement).select?.();
          element.scrollIntoView?.({ block: "nearest" });
        };
        const target = document.getElementById(cellId(rowKey, col));
        if (target) {
          focus(target);
          return;
        }
        for (let near = col - 1; near >= 0; near--) {
          const fallback = document.getElementById(cellId(rowKey, near));
          if (fallback) {
            focus(fallback);
            return;
          }
        }
        if (attempt < 6) focusCell(rowKey, col, attempt + 1);
      });
    },
    [cellId],
  );

  // Първият ред (↓ „отникъде" и ↓/Enter от празна търсачка). false = няма редове.
  const focusFirst = useCallback(
    (col: number) => {
      const first = getRowKeysRef.current()[0];
      if (first == null) return false;
      focusCell(first, col);
      return true;
    },
    [focusCell],
  );

  // Съседната СЪЩЕСТВУВАЩА клетка в посоката (ред-рецепта няма „Партида" —
  // стрелката я прескача, вместо да стои на място). null = ръбът на реда.
  const neighbourCol = useCallback(
    (rowKey: GridRowKey, col: number, step: -1 | 1) => {
      for (let next = col + step; next >= 0 && next < colCount; next += step) {
        if (document.getElementById(cellId(rowKey, next))) return next;
      }
      return null;
    },
    [cellId, colCount],
  );

  // ←/→ от ОТВОРЕН редактор на PickCell: редакторът вече е затворен, самата
  // клетка още не е в DOM — затова търсим съседа от колоната, не от фокуса.
  const focusSideways = useCallback(
    (rowKey: GridRowKey, col: number, step: -1 | 1) => {
      const target = neighbourCol(rowKey, col, step);
      focusCell(rowKey, target ?? col);
    },
    [focusCell, neighbourCol],
  );

  const onGridKey = useCallback(
    (event: React.KeyboardEvent) => {
      // Клетката вече е обработила клавиша (PickCell отваря редактора си)
      if (event.defaultPrevented) return;
      const element = event.target as HTMLElement;
      const rowKeyText = element.dataset?.rk;
      const colText = element.dataset?.c;
      if (rowKeyText == null || colText == null) return;
      if ((event.ctrlKey || event.metaKey) && event.key === "Enter") return;
      const col = +colText;
      const order = getRowKeysRef.current();
      // data-rk е текст — сравняваме като текст, за да работят и числови, и
      // текстови ключове; по-нататък ползваме ИСТИНСКИЯ ключ от реда
      const index = order.findIndex((key) => String(key) === rowKeyText);
      const rowKey = index >= 0 ? order[index] : rowKeyText;
      const last = order.length - 1;

      if (
        event.key === "ArrowDown" ||
        (event.key === "Enter" && !enterOpens.has(col))
      ) {
        event.preventDefault();
        if (index >= 0 && index < last) focusCell(order[index + 1], col);
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        if (index <= 0) onLeaveTopRef.current();
        else focusCell(order[index - 1], col);
        return;
      }
      if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
        const side = event.key === "ArrowLeft" ? "left" : "right";
        if (!atCaretEdge(element, side)) return;
        const target = neighbourCol(rowKey, col, side === "left" ? -1 : 1);
        if (target == null) return;
        event.preventDefault();
        focusCell(rowKey, target);
        return;
      }
      if (event.key === "Escape") {
        // preventDefault + stopPropagation — иначе същият Esc минава и през
        // глобалния handler на страницата (стъпка назад към списъка) и през
        // навигацията на SharedAccountGate
        event.preventDefault();
        event.stopPropagation();
        onEscapeRef.current();
      }
    },
    [enterOpens, focusCell, neighbourCol],
  );

  // Фокусът е в клетка на решетката (за глобалния Esc/↓ на страницата)
  const isInGrid = useCallback(() => {
    const active = document.activeElement as HTMLElement | null;
    return !!active?.dataset && active.dataset.c != null;
  }, []);

  return { cellId, focusCell, focusFirst, focusSideways, onGridKey, isInGrid };
};
